import { useConnection, useWallet } from '@solana/wallet-adapter-react'
import {
  Keypair,
  LAMPORTS_PER_SOL,
  PublicKey,
  Signer,
  SystemProgram,
  Transaction,
  TransactionInstruction,
  TransactionSignature,
} from '@solana/web3.js'
import { deserializeUnchecked } from 'borsh'
import { u8, nu64, struct } from '@solana/buffer-layout'
import { FC, useCallback, useState } from 'react'
import {
  BANANA_MINT,
  BANANA_MINT_AUTHORITY_SEED,
  KONG_LOAN_ACCOUNT_SIZE,
  MARINADE_STATE_ID,
  MSOL_LEG,
  MSOL_LEG_AUTHORITY_INFO,
  MSOL_MINT,
  MSOL_MINT_AUTH,
  PROGRAM_STATE_ACCOUNT,
  PROGRAM_VAULT_SEED,
  RAD_ID,
  RENT_PROGRAM_ID,
  RESERVE_PDA_INFO,
  SOLANA_TOKEN_ID,
  SOL_LEG,
  SOL_ORACLE_PRICE,
  SOL_ORACLE_PRODUCT,
  SPL_ASSOCIATED_TOKEN_ACCOUNT_PROGRAM_ID,
  SYSTEM_PROGRAM_ID,
  MARINADE_PROGRAM_ID,
} from '../../utils/constants'
import { WALLET_ADAPTER_NETWORK } from '../../utils/wallet-config'
import LaunchIcon from '@material-ui/icons/Launch'
import {
  Backdrop,
  Box,
  Button,
  Fade,
  Modal,
  styled,
  TextField,
  Typography,
  Tooltip,
  Grid,
  CircularProgress,
  Link,
} from '@mui/material'
import { makeStyles } from '@material-ui/core'
import ArrowDropDownCircleSharpIcon from '@mui/icons-material/ArrowDropDownCircleSharp'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import { useDispatch, useSelector } from 'react-redux'
import { useNotify } from '../../utils/notify'
import {
  getOpenLoanModal,
  getOpenLoanTransactionSignature,
  setOpenLoanModal,
  setOpenLoanTransactionSignature,
  setScanForNewLoans,
} from '../../redux/modules/loans'
import {
  format5Dec,
  getCollateralAsNumber,
  numLamportsToSol,
  validNumericTrailingCharCheck,
} from '../../utils/helpers'
import { LoanAmountEstimate } from '../LoanAmountEstimate/LoanAmountEstimate'
import { WalletSendTransactionError } from '@solana/wallet-adapter-base'
import { TOKEN_PROGRAM_ID, Token, AccountLayout } from '@solana/spl-token'
import * as marinade from '../../utils/marinade-state'

export const CssTextField = styled(TextField)({
  '& label.MuiInputLabel': {
    color: 'white',
  },
  '& label.MuiInputLabel-root': {
    color: 'white',
  },
  '& label.Mui-focused': {
    color: 'white',
  },
  '& .MuiInput-underline:after': {
    borderBottomColor: 'green',
  },
  '& .MuiInputBase-root': {
    color: 'white',
    backgroundColor: 'transparent',
  },
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: 'green',
    },
    '&:hover fieldset': {
      borderColor: 'white',
    },
    '&.Mui-focused fieldset': {
      borderColor: 'green',
    },
  },
})

export const useStyles = makeStyles({
  link: {
    color: '#ffffff',
    display: 'flex',
    alignItems: 'right',
    textDecoration: 'underline',
    '&:hover': {
      color: '#000000',
    },
  },
  icon: {
    fontSize: 20,
  },
})

async function findAssociatedTokenAddress(
  walletAddress: PublicKey,
  tokenMintAddress: PublicKey
): Promise<PublicKey> {
  return (
    await PublicKey.findProgramAddress(
      [
        walletAddress.toBuffer(),
        TOKEN_PROGRAM_ID.toBuffer(),
        tokenMintAddress.toBuffer(),
      ],
      SPL_ASSOCIATED_TOKEN_ACCOUNT_PROGRAM_ID
    )
  )[0]
}

export const OpenLoan: FC = () => {
  const { connection } = useConnection()
  const { publicKey, sendTransaction } = useWallet()
  const [collateral, setCollateral] = useState('')
  const [loading, setLoading] = useState(false)
  const dispatch = useDispatch()
  const notify = useNotify()
  const open = useSelector(getOpenLoanModal)
  const openLoanTransactionSignature = useSelector(
    getOpenLoanTransactionSignature
  )
  const styles = useStyles()

  const handleClose = useCallback(
    () => dispatch(setOpenLoanModal(false)),
    [dispatch]
  )

  if (!publicKey) {
    dispatch(setOpenLoanModal(false))
  }

  const maxAmountToStake = (
    nativeSOLBalance: number,
    totalStaked: number,
    transactionFee: number,
    marinadeState: marinade.State
  ) => {
    const stakingSolCap =
      marinadeState.staking_sol_cap &&
      Number(
        format5Dec(Number(marinadeState.staking_sol_cap), LAMPORTS_PER_SOL)
      )

    const extraAmountToKeep =
      transactionFee * 4 + marinadeState.rent_exempt_for_token_acc.toNumber()

    const fillCap =
      totalStaked &&
      marinadeState.staking_sol_cap &&
      Number(
        format5Dec(Number(marinadeState.staking_sol_cap), LAMPORTS_PER_SOL)
      ) - Number(format5Dec(totalStaked, LAMPORTS_PER_SOL))

    const maxSolBalance =
      nativeSOLBalance &&
      transactionFee &&
      marinadeState.rent_exempt_for_token_acc &&
      Math.max(
        nativeSOLBalance -
          transactionFee * 4 -
          Number(marinadeState.rent_exempt_for_token_acc),
        0
      ) / LAMPORTS_PER_SOL

    console.log('maxSolBalance: ', maxSolBalance)
    console.log('extraAmountToKeep: ', extraAmountToKeep)
    console.log('fillCap: ', fillCap)
    console.log('Number(format5Dec(totalStaked, LAMPORTS_PER_SOL))')
    console.log(Number(format5Dec(totalStaked, LAMPORTS_PER_SOL)))
    console.log('stakingSolCap: ', stakingSolCap)

    if (
      maxSolBalance + Number(format5Dec(totalStaked, LAMPORTS_PER_SOL)) <
      stakingSolCap
    ) {
      console.log('LESS')
      return maxSolBalance - numLamportsToSol(extraAmountToKeep)
    } else {
      console.log('MORE')
      return fillCap - numLamportsToSol(extraAmountToKeep)
    }
  }

  const createLoan = useCallback(async () => {
    if (!publicKey) {
      notify('error', 'Wallet not connected!')
      return
    }

    let signature: TransactionSignature = ''
    try {
      setLoading(true)
      const owner = publicKey
      const banana_mint_authority = await PublicKey.findProgramAddress(
        [
          PROGRAM_STATE_ACCOUNT.toBuffer(),
          Buffer.from(BANANA_MINT_AUTHORITY_SEED),
        ],
        RAD_ID
      )

      const loan_account = new Keypair()

      const bananaAtaAddress = await findAssociatedTokenAddress(
        owner,
        BANANA_MINT
      )

      const userMSolAtaAddress = await findAssociatedTokenAddress(
        owner,
        MSOL_MINT
      )

      const program_vault_derived_pubKey = await PublicKey.createWithSeed(
        loan_account.publicKey,
        PROGRAM_VAULT_SEED,
        TOKEN_PROGRAM_ID
      )

      const programVaultAuthority = await PublicKey.findProgramAddress(
        [
          loan_account.publicKey.toBuffer(),
          program_vault_derived_pubKey.toBuffer(),
        ],
        RAD_ID
      )

      const transaction = new Transaction()

      const openLoanTransactionItems = [
        // create loan account
        SystemProgram.createAccount({
          fromPubkey: owner,
          newAccountPubkey: loan_account.publicKey,
          lamports: await connection.getMinimumBalanceForRentExemption(
            KONG_LOAN_ACCOUNT_SIZE
          ),
          space: KONG_LOAN_ACCOUNT_SIZE,
          programId: RAD_ID,
        }),
        // CREATE PROGRAM VAULT
        SystemProgram.createAccountWithSeed({
          basePubkey: loan_account.publicKey,
          fromPubkey: owner,
          newAccountPubkey: program_vault_derived_pubKey,
          seed: PROGRAM_VAULT_SEED,
          lamports: await connection.getMinimumBalanceForRentExemption(
            AccountLayout.span
          ),
          space: AccountLayout.span,
          programId: TOKEN_PROGRAM_ID,
        }),
        // initialize program vault as MSOL
        Token.createInitAccountInstruction(
          TOKEN_PROGRAM_ID,
          MSOL_MINT,
          program_vault_derived_pubKey,
          programVaultAuthority[0]
        ),
      ]

      // check if ataAccountExists
      const ataAccountInfo = await connection.getAccountInfo(
        bananaAtaAddress,
        'finalized'
      )

      if (!ataAccountInfo) {
        let dataLayoutATA = struct([u8('instruction')])
        const ataData = Buffer.alloc(dataLayoutATA.span)
        dataLayoutATA.encode(
          {
            instruction: 0,
          },
          ataData
        )
        openLoanTransactionItems.push(
          new TransactionInstruction({
            keys: [
              { pubkey: owner, isSigner: false, isWritable: true },
              { pubkey: bananaAtaAddress, isSigner: false, isWritable: true },
              { pubkey: owner, isSigner: false, isWritable: false },
              {
                pubkey: BANANA_MINT,
                isSigner: false,
                isWritable: false,
              },
              { pubkey: SYSTEM_PROGRAM_ID, isSigner: false, isWritable: false },
              { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
              { pubkey: RENT_PROGRAM_ID, isSigner: false, isWritable: false },
            ],
            programId: SPL_ASSOCIATED_TOKEN_ACCOUNT_PROGRAM_ID,
            data: ataData,
          })
        )
      }

      // check if userMSolAtaAddress exists
      const userMSolAtaAccountInfo = await connection.getAccountInfo(
        userMSolAtaAddress,
        'finalized'
      )

      if (!userMSolAtaAccountInfo) {
        let dataLayoutMSolATA = struct([u8('instruction')])
        const mSolAtaData = Buffer.alloc(dataLayoutMSolATA.span)
        dataLayoutMSolATA.encode(
          {
            instruction: 0,
          },
          mSolAtaData
        )
        openLoanTransactionItems.push(
          new TransactionInstruction({
            keys: [
              { pubkey: owner, isSigner: false, isWritable: true },
              { pubkey: userMSolAtaAddress, isSigner: false, isWritable: true },
              { pubkey: owner, isSigner: false, isWritable: false },
              {
                pubkey: MSOL_MINT,
                isSigner: false,
                isWritable: false,
              },
              { pubkey: SYSTEM_PROGRAM_ID, isSigner: false, isWritable: false },
              { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
              { pubkey: RENT_PROGRAM_ID, isSigner: false, isWritable: false },
            ],
            programId: SPL_ASSOCIATED_TOKEN_ACCOUNT_PROGRAM_ID,
            data: mSolAtaData,
          })
        )
      }

      let dataLayout = struct([u8('instruction'), nu64('lamports')])
      const data = Buffer.alloc(dataLayout.span)
      dataLayout.encode(
        {
          instruction: 0,
          lamports: getCollateralAsNumber(collateral) * LAMPORTS_PER_SOL,
        },
        data
      )
      openLoanTransactionItems.push(
        // send create loan instruction
        new TransactionInstruction({
          keys: [
            {
              pubkey: PROGRAM_STATE_ACCOUNT,
              isSigner: false,
              isWritable: false,
            },
            { pubkey: MARINADE_STATE_ID, isSigner: false, isWritable: true },
            {
              pubkey: loan_account.publicKey,
              isSigner: false,
              isWritable: true,
            },
            {
              pubkey: program_vault_derived_pubKey,
              isSigner: false,
              isWritable: true,
            },
            { pubkey: BANANA_MINT, isSigner: false, isWritable: true },
            {
              pubkey: banana_mint_authority[0],
              isSigner: false,
              isWritable: false,
            },
            {
              pubkey: bananaAtaAddress,
              isSigner: false,
              isWritable: true,
            },
            { pubkey: owner, isSigner: true, isWritable: true },
            { pubkey: userMSolAtaAddress, isSigner: false, isWritable: true },
            { pubkey: owner, isSigner: true, isWritable: true },
            {
              pubkey: SOL_ORACLE_PRODUCT,
              isSigner: false,
              isWritable: false,
            },
            { pubkey: SOL_ORACLE_PRICE, isSigner: false, isWritable: false },
            { pubkey: MSOL_MINT, isSigner: false, isWritable: true },
            { pubkey: SOL_LEG, isSigner: false, isWritable: true },
            { pubkey: MSOL_LEG, isSigner: false, isWritable: true },
            {
              pubkey: MSOL_LEG_AUTHORITY_INFO,
              isSigner: false,
              isWritable: false,
            },
            { pubkey: RESERVE_PDA_INFO, isSigner: false, isWritable: true },
            { pubkey: MSOL_MINT_AUTH, isSigner: false, isWritable: false },
            { pubkey: SOLANA_TOKEN_ID, isSigner: false, isWritable: false },
            { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
            {
              pubkey: MARINADE_PROGRAM_ID,
              isSigner: false,
              isWritable: false,
            },
          ],
          programId: RAD_ID,
          data,
        })
      )

      transaction.add(...openLoanTransactionItems)

      const walletBalance = await connection.getBalance(publicKey)
      const walletBalanceSOL = walletBalance / LAMPORTS_PER_SOL
      if (walletBalanceSOL < getCollateralAsNumber(collateral)) {
        throw Error(
          `Insufficient wallet balance of ${walletBalanceSOL.toFixed(
            5
          )} SOL for transaction of ${collateral} SOL.`
        )
      }

      // // Check Marinade
      const stateAccount = await connection.getAccountInfo(MARINADE_STATE_ID)
      const state: marinade.State = deserializeUnchecked(
        marinade.MARINADE_BORSH_SCHEMA,
        marinade.State,
        stateAccount!.data.slice(8)
      )

      const totalStaked = state?.validator_system?.total_active_balance
        ?.add(state?.stake_system.total_cooling_down)
        ?.add(state?.available_reserve_balance)
        // ?.sub(state?.circulating_ticket_balance)
        ?.toNumber()

      const transactionFee = (await connection.getRecentBlockhash())
        .feeCalculator.lamportsPerSignature

      if (
        Number(collateral) + Number(format5Dec(totalStaked, LAMPORTS_PER_SOL)) >
        Number(format5Dec(Number(state?.staking_sol_cap), LAMPORTS_PER_SOL))
      ) {
        const max = maxAmountToStake(
          walletBalance,
          totalStaked,
          transactionFee,
          state
        )
        setCollateral(max.toString())
        notify(
          'warning',
          'Marinade Staking Pool is limiting loan collateral to: ' +
            max.toString()
        )
        setLoading(false)
        dispatch(setOpenLoanTransactionSignature(null))
        return
      }
      // END Check

      const signers: Signer[] = [loan_account]

      notify('info', 'Open Loan Request Approval Needed')
      signature = await sendTransaction(transaction, connection, {
        signers: signers,
      })
      notify('info', 'Transaction sent:', signature)
      dispatch(setOpenLoanTransactionSignature(signature))

      await connection.confirmTransaction(signature, 'finalized')

      notify('success', 'Transaction successful!', signature)
      dispatch(setScanForNewLoans(true))
      dispatch(setOpenLoanTransactionSignature(null))
      handleClose()
      setLoading(false)
      setCollateral('')
    } catch (error: any) {
      if (error instanceof WalletSendTransactionError) {
        notify('error', `Transaction failed! ${error?.message}`, signature)
        setLoading(false)
        dispatch(setOpenLoanTransactionSignature(null))
        return
      } else {
        console.log(error)
        notify('error', `Transaction failed! ${error?.message}`, signature)
        setLoading(false)
        dispatch(setOpenLoanTransactionSignature(null))
        return
      }
    }
  }, [
    publicKey,
    sendTransaction,
    connection,
    collateral,
    dispatch,
    notify,
    handleClose,
  ])

  function setCollateralAmount(ev: React.ChangeEvent<HTMLInputElement>) {
    if (validNumericTrailingCharCheck(ev.target.value)) {
      setCollateral(validNumericTrailingCharCheck(ev.target.value))
    } else if (ev.target.value === '') {
      setCollateral('')
    }
  }

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={open}
      onClose={handleClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
        onClick: undefined,
      }}
    >
      <Fade in={open}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            border: '2px solid #000',
            boxShadow: 24,
            p: 4,
          }}
          className="Open-loan-modal"
        >
          <Typography
            id="modal-modal-title"
            variant="h6"
            component="h2"
            align={'center'}
            color={'white'}
          >
            Open a New Loan
          </Typography>
          <br />
          <CssTextField
            variant="outlined"
            autoFocus={true}
            fullWidth={true}
            type={'text'}
            label="Collateral Amount in SOL"
            id="collateral-amount"
            value={collateral}
            onChange={setCollateralAmount.bind(this)}
          />
          <br />
          <br />
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <ArrowDropDownCircleSharpIcon
              style={{ marginLeft: '45%' }}
              color="primary"
              fontSize="large"
            />
          </div>
          <br />
          <LoanAmountEstimate collateral={collateral} />
          <br />
          <br />
          <Box sx={{ flexGrow: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={10}>
                <Typography
                  id="modal-modal-title"
                  variant="caption"
                  component="h2"
                  align={'left'}
                  color={'white'}
                >
                  Slippage Tolerance
                  <Tooltip
                    title="The maximum difference between your estimated price and execution price."
                    placement="right"
                  >
                    <HelpOutlineIcon
                      style={{
                        position: 'relative',
                        marginLeft: '2px',
                        top: '5px',
                      }}
                      fontSize="small"
                      color={'primary'}
                    />
                  </Tooltip>
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography
                  id="modal-modal-title"
                  variant="caption"
                  component="h2"
                  align={'right'}
                  color={'white'}
                  style={{ position: 'relative', top: '5px' }}
                >
                  0.5%
                </Typography>
              </Grid>
            </Grid>
          </Box>
          <br />
          <br />
          {loading ? (
            <Box sx={{ flexGrow: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={7}>
                  <CircularProgress color="success" />
                </Grid>
                <Grid item xs={5}>
                  {openLoanTransactionSignature && (
                    <Link
                      className={styles.link}
                      href={`https://explorer.solana.com/tx/${openLoanTransactionSignature}?cluster=${WALLET_ADAPTER_NETWORK}`}
                      target="_blank"
                      align="right"
                    >
                      Transaction
                      <LaunchIcon className={styles.icon} />
                    </Link>
                  )}
                </Grid>
              </Grid>
            </Box>
          ) : (
            <>
              <Button onClick={createLoan} variant={'contained'}>
                Open Loan
              </Button>
              &nbsp;&nbsp;&nbsp;
              <Button
                onClick={handleClose}
                variant={'contained'}
                color={'warning'}
              >
                Cancel
              </Button>
            </>
          )}
        </Box>
      </Fade>
    </Modal>
  )
}
